import * as React from "react";
import { useRef, useEffect } from 'react';
import Typed from 'typed.js';
import useSound from '../hooks/useSound';

export interface TypeWriterProps {
  strings: string[];
  speed?: number;
  startDelay?: number;
  backSpeed?: number;
  backDelay?: number;
  loop?: boolean;
  className?: string;
  cursorClassName?: string;
  soundEnabled?: boolean;
}

const TypeWriter: React.FC<TypeWriterProps> = ({ 
  strings = [], 
  speed = 50, 
  startDelay = 300, 
  backSpeed = 30,
  backDelay = 1000,
  loop = true,
  className = '',
  cursorClassName = '',
  soundEnabled = true 
}) => {
  const typeTarget = useRef<HTMLSpanElement>(null);
  const typed = useRef<Typed | null>(null);
  const { playTyping } = useSound();
  
  useEffect(() => {
    if (!typeTarget.current) return;

    // Initialize Typed.js
    const options = {
      strings: strings,
      typeSpeed: speed,
      startDelay: startDelay,
      backSpeed: backSpeed,
      backDelay: backDelay,
      loop: loop,
      cursorChar: '|',
      preStringTyped: () => {
        if (soundEnabled) playTyping();
      },
      onStringTyped: () => {
        if (soundEnabled) playTyping();
      },
      onStart: () => {
        if (soundEnabled) playTyping();
      }
    };

    typed.current = new Typed(typeTarget.current, options);
    
    // Apply custom cursor class if provided
    if (cursorClassName) {
      const cursorElement = typeTarget.current.parentElement?.querySelector('.typed-cursor');
      if (cursorElement) {
        cursorElement.className = `typed-cursor ${cursorClassName}`;
      }
    }
    
    // Cleanup
    return () => {
      if (typed.current) {
        typed.current.destroy();
      }
    };
  }, [strings, speed, startDelay, backSpeed, backDelay, loop, playTyping, soundEnabled, cursorClassName]);
  
  return (
    <span 
      ref={typeTarget} 
      className={`typewriter ${className}`}
      style={{
        color: '#00ff00',
        textShadow: '0 0 5px #00ff00'
      }}
    ></span>
  );
};

export default TypeWriter;